import { User, MoreHorizontal, Shield, Pencil, Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn, formatDateTime } from '@/lib/utils';
import type { ApplicationUser, UserRole } from '@/types';

interface UserTableProps {
  users: ApplicationUser[];
  currentUserId: string;
  onEditRole: (user: ApplicationUser) => void;
  onRemove: (user: ApplicationUser) => void;
  className?: string | undefined;
}

const roleConfig: Record<UserRole, {
  label: string;
  variant: 'default' | 'secondary' | 'outline';
  description: string;
}> = {
  MigrationAdmin: {
    label: 'Admin',
    variant: 'default',
    description: 'Full access to all features',
  },
  MigrationOperator: {
    label: 'Operator',
    variant: 'secondary',
    description: 'Can start and manage migrations',
  },
  MigrationViewer: {
    label: 'Viewer',
    variant: 'outline',
    description: 'Read-only access',
  },
};

export function UserTable({
  users,
  currentUserId,
  onEditRole,
  onRemove,
  className,
}: UserTableProps) {
  return (
    <div className={cn('rounded-md border', className)}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Added</TableHead>
            <TableHead>Last Login</TableHead>
            <TableHead className="w-[70px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                No users found.
              </TableCell>
            </TableRow>
          ) : (
            users.map((user) => {
              const role = roleConfig[user.role];
              const isCurrentUser = user.id === currentUserId;

              return (
                <TableRow
                  key={user.id}
                  className={cn(!user.isActive && 'opacity-60')}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
                        <User className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {user.displayName}
                          {isCurrentUser && (
                            <span className="ml-2 text-xs text-muted-foreground">(You)</span>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={role.variant} className="gap-1">
                      <Shield className="h-3 w-3" />
                      {role.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {user.createdAt ? formatDateTime(user.createdAt) : '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never'}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => onEditRole(user)}
                          disabled={isCurrentUser}
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Change Role
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onRemove(user)}
                          disabled={isCurrentUser}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Remove User
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
